import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleNotch } from "@phosphor-icons/react";
import { VideoForm, type VideoFormValues } from "@/components/admin/videos/VideoForm";
import { videoByIdQueryOptions } from "@/api-queries/videos";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import type { VideoStatus } from "@/types";

export const Route = createFileRoute("/dashboard/admin/videos/$videoId")({
  component: AdminEditVideo,
});

function AdminEditVideo() {
  const { videoId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: video, isLoading, error } = useQuery(videoByIdQueryOptions(videoId));

  const update = useMutation({
    mutationFn: async ({ values, status }: { values: VideoFormValues; status: VideoStatus }) => {
      const res = await fetch(`/api/videos/by-id/${videoId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values, title: values.title.trim(), posterUrl: values.posterUrl || null, status }),
      });
      await throwIfNotOk(res, "Spremanje nije uspjelo");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["videos"] });
      toast.success("Video sačuvan");
      navigate({ to: "/dashboard/admin/videos" });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <CircleNotch className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error || !video) return <p className="py-20 text-center text-muted-foreground">Video nije pronađen.</p>;

  return <VideoForm initial={video} isSubmitting={update.isPending} onSubmit={(values, status) => update.mutate({ values, status })} />;
}
